import React, { useState, useContext } from 'react';
import { Form } from 'formik';
import * as yup from 'yup';
import { useSelector, } from 'react-redux';
import moment from 'moment';
import { Redirect, useHistory } from 'react-router-dom';

import { FormStep } from 'types/forms/formTypes';
import { DroneAccreditationFormData } from 'types/forms/droneAccreditationTypes';
import { Container, Loader } from 'components/common';
import {
  FormAlert,
  GenericStep,
  ApplicationName,
  Declaration,
  YouTubeVideo
} from 'components/CASAForm';
import {
  AuBreadcrumbs,
  IAuBreadcrumbItem
} from 'components/DTA';
import { AssessmentQuestion, AssessmentResult } from 'forms/Lms';

import { FormContext, FormContextProvider, IFormContextType } from 'contexts/FormContext';
import { LmsContext, AccreditationConstants } from 'contexts/LmsContext';

import { featureSelector } from 'PortalStore/Selectors/EnvDataSelectors';

import useLmsService from 'services/lmsService/useLmsService';

import { FORM_ERROR_MESSAGES } from 'internalConstants';

const RecreationalAccreditation: React.FC = () => {
  const history = useHistory();
  const { getAssessmentDataRequest, assessment, questions, checkAnswers, dashboardUrl, setPercentCorrect, setPassed, setSubmissionInProgress, setAssessment } = useContext(LmsContext);
  const [currentQuestionIndex, setCurrentQuestionIndex] = useState<number>(0);
  const [videoPlayed, setVideoPlayed] = useState<boolean>(false);
  const lmsService = useLmsService();

  const featureAccredRec = useSelector(state => featureSelector(state, 'Portal - Feature Availability - Recreational Drone'));
  const recAccredDate = moment(featureAccredRec, 'DD/MM/YYYY');
  const recAvailable = recAccredDate.isBefore();

  const initialValues: DroneAccreditationFormData = {
    answers: []
  };

  // Steps and step validation associated with the form
  const RECREATIONAL_STEPS: FormStep[] = [
    {
      id: 'RecreationalDecleration',
      render: () => (
        <GenericStep
          nextButtonText='Begin accreditation test'
          hidePrevious
          showSaveAndExit={false}
          triggerFormValidation={true}
          customHandleBack={(e: React.MouseEvent<HTMLElement>) => {
            e.preventDefault();
            history.goBack();
          }}>
          <p>For recreational drone operators, accreditation involves a short video and a series of questions related to the drone safety rules.</p>
          <p>Complete the declaration below to begin the accreditation.</p>

          <Declaration
            label='I agree and understand the above conditions.'
            fullWidth>
            <p>I declare that I am taking this accreditation test as myself. I acknowledge that giving false or misleading information to CASA in this application is an offence under Part 7.4 of the Criminal Code Act 1995.</p>
          </Declaration>
        </GenericStep>
      ),
      validationSchema: yup.object().shape({
        declaration: yup.boolean()
          .required(FORM_ERROR_MESSAGES.DECLARATION)
          .oneOf([true], FORM_ERROR_MESSAGES.DECLARATION)
      })
    },
    {
      id: 'RecreationalInfo',
      render: () => (
        <GenericStep
          hidePrevious
          showSaveAndExit={false}>
          <h2>What is recreational drone operator accreditation?</h2>
          <p>People operating drones for sport and recreation do not require a remote pilot licence or commercial RPA accreditation. An accreditation is an authorisation similar to a licence. There are certain rules regarding where, when and how these model aircraft can be flown.</p>
          <p>Before you operate a drone:</p>
          <ul>
            <li>ensure you hold a valid accreditation (throughout this process), and</li>
            <li>ensure the drone you are flying is registered (either by yourself or someone else)</li>
          </ul>
          <h2>Accreditation outcomes</h2>
          <p>If you pass you will:</p>
          <ul>
            <li>be accredited to operate a drone for sport or recreation</li>
            <li>be accredited to supervise people under 16 years of age to operate a drone provided you are 18 or older</li>
          </ul>
        </GenericStep>
      )
    },
    {
      id: 'IntroVideo',
      render: () => (
        <GenericStep
          hidePrevious
          hideNext={!videoPlayed}
          showSaveAndExit={false}
          nextButtonText='Go to question one'>
          <h2>Recreational drone operator accreditation video</h2>
          <p>Before you operate a model aircraft (a drone weighing more than 250 g) for sport or recreation, you must demonstrate an understanding of the safety rules that apply. Breaking these rules can land you in trouble and criminal penalties may apply.</p>
          <p>The following educational safety video provides an overview of the safety rules that apply to recreational drone operation. In addition to watching this safety video, you can visit our website for more detailed information. There are 12 questions and to achieve accreditation, you must answer 11 correctly.</p>

          <div className='mt-4'>
            {
              assessment &&
              <YouTubeVideo
                videoUrl={assessment.videoLink}
                onEnd={() => setVideoPlayed(true)} />
            }
          </div>
        </GenericStep>
      )
    },
    {
      id: 'AssessmentQuestion',
      render: () => <AssessmentQuestion
        currentQuestionIndex={currentQuestionIndex}
        setCurrentQuestionIndex={setCurrentQuestionIndex}
        submitLmsForm={handleSubmit} />,
      validationSchema: yup.object().shape({
        answers: yup.array().of(
          yup.object().shape({
            questionId: yup.string()
              .required(FORM_ERROR_MESSAGES.REQUIRED_FIELD),
            answerIds: yup.array()
              .of(yup.string())
              .min(1, FORM_ERROR_MESSAGES.REQUIRED_FIELD)
              .required(FORM_ERROR_MESSAGES.REQUIRED_FIELD)
          })
        )
      })
    },
    {
      id: 'AssessmentResult',
      render: () => <AssessmentResult resetQuestionIndex={() => setCurrentQuestionIndex(0)} />
    }
  ];

  /**
   * Handle Submit
   *
   * @description Handles LMS Submission when all questions answered
   * @async
   * @param {DroneAccreditationFormData} values DroneAccrediation form data
   * @returns {Promise<void>} Calls service no return
   */
  const handleSubmit = async (values: DroneAccreditationFormData): Promise<void> => {
    const correctQuestions = questions.filter((question, index) => checkAnswers(question, values.answers[index]?.answerIds));

    const percentCorrect = Math.round(correctQuestions.length / questions.length * 100);
    const passed = percentCorrect >= (assessment && assessment.passMark ? assessment.passMark : 80);

    // Send request to Submission service
    setSubmissionInProgress(true);
    await lmsService.recreationalSubmission({
      formData: {
        assessmentType: AccreditationConstants.RECREATIONAL_ASSESSMENT_TYPE,
        assessmentPassed: passed,
        assessmentResult: percentCorrect,
        answers: values.answers.map(answer => ({ questionId: answer.questionId, answerIds: typeof (answer.answerIds) === 'string' ? [answer.answerIds] : answer.answerIds }))
      }
    });
    setSubmissionInProgress(false);
    setPercentCorrect(percentCorrect);
    setPassed(passed);
    if (!passed) {
      // accreditation failed reload assessment details and set into the context
      const assessmentDataRequest = await lmsService.getAssessmentData(getAssessmentDataRequest);
      if (assessmentDataRequest && assessmentDataRequest.wasSuccessful && assessmentDataRequest.assessment) {
        setAssessment(assessmentDataRequest.assessment);
      }
    }
  };

  {/* TODO: handle data load errors */ }
  if (!assessment) {
    return <Loader message='Working. Please wait.....' />;
  }

  if (!recAvailable) {
    return <Redirect to='/drone-accreditation' />;
  }

  const breadcrumbs: IAuBreadcrumbItem[] = [
    { link: dashboardUrl, text: 'myCASA' },
    { text: 'Drone accreditation' }
  ];

  return (
    <Container>
      <AuBreadcrumbs label='breadcrumbs' items={breadcrumbs} />
      <FormContextProvider
        initialValues={initialValues}
        handleSubmit={handleSubmit}
        steps={RECREATIONAL_STEPS}>
        <FormContext.Consumer>
          {({ currentStepIndex, steps, currentStepProps }: IFormContextType) => (
            <Form name='Drone accreditation'>
              <FormAlert htmlFor='Drone accreditation' />
              <ApplicationName name='Recreational drone operator accreditation' />
              {steps[currentStepIndex].render(currentStepProps)}
            </Form>
          )}
        </FormContext.Consumer>
      </FormContextProvider>
    </Container>
  );
};

export default RecreationalAccreditation;